import React from 'react';
import { Link } from 'react-router-dom';
import { Plane, Shield, Lock, Eye, Database, UserCheck, Mail } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1A2B4A] via-[#243552] to-[#1A2B4A]">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-[#1A2B4A]/95 backdrop-blur-sm border-b border-[#4A90E2]/20 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <Plane className="h-8 w-8 text-[#4A90E2]" />
              <span className="text-2xl font-heading font-bold text-white">Winglet</span>
            </Link>
            <div className="hidden md:flex space-x-8">
              <Link to="/how-it-works" className="text-gray-300 hover:text-[#4A90E2] transition-colors">How It Works</Link>
              <Link to="/pricing" className="text-gray-300 hover:text-[#4A90E2] transition-colors">Pricing</Link>
              <Link to="/compare-tools" className="text-gray-300 hover:text-[#4A90E2] transition-colors">Compare</Link>
              <Link to="/about" className="text-gray-300 hover:text-[#4A90E2] transition-colors">About</Link>
              <Link to="/data-privacy" className="text-gray-300 hover:text-[#4A90E2] transition-colors">Data Privacy</Link>
            </div>
            <div className="flex space-x-4">
              <Link to="/login" className="text-gray-300 hover:text-white transition-colors">Sign In</Link>
              <Link to="/signup" className="bg-[#4A90E2] hover:bg-[#3A7BC8] text-white px-6 py-2 rounded-lg transition-colors">
                Start Free Trial
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <Shield className="h-16 w-16 text-[#4A90E2] mx-auto mb-6" />
          <h1 className="text-5xl md:text-6xl font-heading font-bold text-white mb-6">
            Privacy <span className="text-[#4A90E2]">Policy</span>
          </h1>
          <p className="text-xl text-gray-300 mb-4">
            Your rotation data, pay information, and personal details are protected with military-grade encryption.
          </p>
          <p className="text-sm text-gray-400">
            Last Updated: January 23, 2026
          </p>
        </div>
      </section>

      {/* Quick Summary */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#1A2B4A] border border-[#4A90E2]/30 rounded-xl p-8">
            <h2 className="text-2xl font-heading font-bold text-white mb-6">TL;DR - The Important Stuff</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <SummaryItem 
                icon={<Lock className="h-6 w-6" />}
                title="AES-256 Encryption"
                description="Your rotation and timecard data is encrypted at rest and in transit—same standard banks use."
              />
              <SummaryItem 
                icon={<Eye className="h-6 w-6" />}
                title="We Never Sell Your Data"
                description="Your pay info, rotations, and personal details are yours. We don't sell, share, or monetize them."
              />
              <SummaryItem 
                icon={<Database className="h-6 w-6" />}
                title="Secure Cloud Storage"
                description="Hosted on AWS/Supabase with SOC 2 compliance and automatic backups."
              />
              <SummaryItem 
                icon={<UserCheck className="h-6 w-6" />}
                title="You Control Your Data"
                description="Download or delete your data anytime. No questions asked."
              />
            </div>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#0F1824] border border-[#4A90E2]/30 rounded-xl p-8">
            <h2 className="text-2xl font-heading font-bold text-white mb-6">Table of Contents</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <TOCLink href="#what-we-collect" text="1. What We Collect" />
              <TOCLink href="#how-we-use" text="2. How We Use Your Data" />
              <TOCLink href="#how-we-protect" text="3. How We Protect Your Data" />
              <TOCLink href="#who-we-share-with" text="4. Who We Share Data With" />
              <TOCLink href="#your-rights" text="5. Your Rights" />
              <TOCLink href="#data-retention" text="6. Data Retention" />
              <TOCLink href="#cookies" text="7. Cookies & Tracking" />
              <TOCLink href="#children" text="8. Children's Privacy" />
              <TOCLink href="#changes" text="9. Changes to This Policy" />
              <TOCLink href="#contact" text="10. Contact Us" />
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto space-y-12">
          {/* Section 1 */}
          <PolicySection 
            id="what-we-collect"
            title="1. What We Collect"
            content={
              <>
                <p className="mb-4">
                  Winglet collects only the information necessary to provide accurate pay calculations and FAR 117 compliance checking:
                </p>
                <div className="space-y-4">
                  <DataCategory 
                    title="Account Information"
                    items={[
                      "Email address (for login and notifications)",
                      "Password (encrypted with bcrypt)",
                      "Employee ID (to link rotations and timecards)",
                      "Full name (for personalization)",
                      "Pay rate, position, aircraft type (for calculations)"
                    ]}
                  />
                  <DataCategory 
                    title="Rotation & Flight Data"
                    items={[
                      "Rotation PDFs or text you upload",
                      "Flight numbers, departure/arrival airports, times",
                      "Duty periods, layover hotels, TAFB hours",
                      "Deadhead segments and equipment types",
                      "FAR 117 compliance analysis results"
                    ]}
                  />
                  <DataCategory 
                    title="Timecard Data"
                    items={[
                      "Company timecard PDFs or text you upload",
                      "Credit hours, pay hours, per diem",
                      "Rotation totals and line items",
                      "Variance detection results"
                    ]}
                  />
                  <DataCategory 
                    title="Usage Data"
                    items={[
                      "Login times and IP addresses (for security)",
                      "Pages visited (to improve user experience)",
                      "Feature usage (to prioritize development)",
                      "Error logs (to fix bugs)"
                    ]}
                  />
                </div>
              </>
            }
          />

          {/* Section 2 */}
          <PolicySection 
            id="how-we-use"
            title="2. How We Use Your Data"
            content={
              <>
                <p className="mb-4">
                  We use your information solely to provide Winglet's core services:
                </p>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">✓</span>
                    <span><strong className="text-white">Pay Calculations:</strong> Calculate credit hours, apply PWA Section 12 rules, detect variances</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">✓</span>
                    <span><strong className="text-white">FAR 117 Compliance:</strong> Check duty periods, rest requirements, rolling limits</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">✓</span>
                    <span><strong className="text-white">Reroute Analysis:</strong> Classify reroutes (GREEN/YELLOW/RED), calculate Section 23L premiums</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">✓</span>
                    <span><strong className="text-white">Variance Detection:</strong> Compare your calculations vs company timecard, flag discrepancies</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">✓</span>
                    <span><strong className="text-white">Notifications:</strong> Send alerts for pay variances, FAR 117 warnings, flight changes</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">✓</span>
                    <span><strong className="text-white">Support:</strong> Troubleshoot issues, answer questions, assist with grievances</span>
                  </li>
                </ul>
                <div className="mt-6 bg-[#0F1824] border border-amber-500/30 rounded-lg p-4">
                  <p className="text-white font-semibold mb-2">What We DON'T Do:</p>
                  <ul className="space-y-2 text-gray-300 text-sm">
                    <li>❌ Sell your data to third parties</li>
                    <li>❌ Share your rotation details with other pilots or airlines</li>
                    <li>❌ Use your data for advertising or marketing to others</li>
                    <li>❌ Train AI models on your personal flight data</li>
                  </ul>
                </div>
              </>
            }
          />

          {/* Section 3 */}
          <PolicySection 
            id="how-we-protect"
            title="3. How We Protect Your Data"
            content={
              <>
                <p className="mb-4 text-white font-semibold">
                  Military-grade security, period.
                </p>
                <div className="space-y-4">
                  <SecurityMeasure 
                    icon={<Lock className="h-6 w-6" />}
                    title="AES-256 Encryption"
                    description="All rotation and timecard data is encrypted at rest using AES-256—the same standard the U.S. government uses for classified information. Even if someone gained access to our servers, your data would be unreadable."
                  />
                  <SecurityMeasure 
                    icon={<Shield className="h-6 w-6" />}
                    title="TLS/SSL in Transit"
                    description="All data transmitted between your browser and our servers uses TLS 1.3 encryption. No one can intercept your rotation uploads or timecard data."
                  />
                  <SecurityMeasure 
                    icon={<Database className="h-6 w-6" />}
                    title="SOC 2 Compliant Hosting"
                    description="Winglet is hosted on AWS and Supabase, both SOC 2 Type II certified for security, availability, and confidentiality. Automatic daily backups ensure your data is never lost."
                  />
                  <SecurityMeasure 
                    icon={<UserCheck className="h-6 w-6" />}
                    title="Password Security"
                    description="Passwords are hashed with bcrypt (14 rounds) and never stored in plain text. We can't see your password—only you know it."
                  />
                </div>
                <div className="mt-6 bg-[#1A2B4A] border border-[#4A90E2]/30 rounded-lg p-4">
                  <p className="text-gray-300 text-sm">
                    <strong className="text-white">Pilot-Specific Security:</strong> Your rotation data contains sensitive information (pay rates, schedules, hotels). We treat it like bank account data—encrypted, access-controlled, and never shared.
                  </p>
                </div>
              </>
            }
          />

          {/* Section 4 */}
          <PolicySection 
            id="who-we-share-with"
            title="4. Who We Share Data With"
            content={
              <>
                <p className="mb-4">
                  Short answer: <strong className="text-[#4A90E2]">Almost no one.</strong>
                </p>
                <p className="mb-4 text-gray-300">
                  We share data only in these limited circumstances:
                </p>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">•</span>
                    <span><strong className="text-white">Service Providers:</strong> AWS (hosting), Supabase (database), Stripe (payments). These companies have strict confidentiality agreements and only process data on our behalf—they don't own or use it.</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">•</span>
                    <span><strong className="text-white">Legal Requirements:</strong> If required by court order, subpoena, or law enforcement request. We'll notify you unless prohibited by law.</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">•</span>
                    <span><strong className="text-white">Aggregated Statistics:</strong> We may share anonymized, aggregate data (e.g., "Average pilot catches 3-5 errors/year") for marketing or industry reports. No individual pilot data is ever included.</span>
                  </li>
                </ul>
                <div className="mt-6 bg-[#0F1824] border border-green-500/30 rounded-lg p-4">
                  <p className="text-white font-semibold mb-2">We Will NEVER:</p>
                  <ul className="space-y-2 text-gray-300 text-sm">
                    <li>✓ Sell your rotation or timecard data to airlines, scheduling vendors, or data brokers</li>
                    <li>✓ Share your pay rates or employee ID with third parties</li>
                    <li>✓ Give your data to advertising networks or analytics companies</li>
                  </ul>
                </div>
              </>
            }
          />

          {/* Section 5 */}
          <PolicySection 
            id="your-rights"
            title="5. Your Rights (GDPR/CCPA Compliance)"
            content={
              <>
                <p className="mb-4">
                  You have complete control over your data:
                </p>
                <div className="space-y-4">
                  <RightItem 
                    title="Right to Access"
                    description="Download all your data (rotations, timecards, calculations) in JSON or CSV format anytime."
                  />
                  <RightItem 
                    title="Right to Delete"
                    description="Delete your account and all associated data permanently. We'll remove everything within 30 days."
                  />
                  <RightItem 
                    title="Right to Correct"
                    description="Update your pay rate, employee ID, or personal information at any time in Settings."
                  />
                  <RightItem 
                    title="Right to Portability"
                    description="Export your data to take to another service or for your own records."
                  />
                  <RightItem 
                    title="Right to Opt-Out"
                    description="Disable email notifications, analytics tracking, or any non-essential data collection."
                  />
                </div>
                <p className="mt-6 text-gray-300">
                  To exercise any of these rights, email <a href="mailto:privacy@wingletapp.com" className="text-[#4A90E2] hover:text-white">privacy@wingletapp.com</a> or use the Settings page in your account.
                </p>
              </>
            }
          />

          {/* Section 6 */}
          <PolicySection 
            id="data-retention"
            title="6. Data Retention"
            content={
              <>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">•</span>
                    <span><strong className="text-white">Active Accounts:</strong> We keep your rotation and timecard data as long as your account is active. You can delete individual rotations or timecards anytime.</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">•</span>
                    <span><strong className="text-white">Deleted Accounts:</strong> When you delete your account, all personal data is permanently removed within 30 days. We may retain anonymized aggregate statistics for analytics.</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">•</span>
                    <span><strong className="text-white">Backups:</strong> Encrypted backups are retained for 90 days for disaster recovery, then permanently deleted.</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">•</span>
                    <span><strong className="text-white">Legal Holds:</strong> If your data is subject to a legal hold (e.g., ongoing grievance or lawsuit), we'll retain it until the hold is lifted.</span>
                  </li>
                </ul>
              </>
            }
          />

          {/* Section 7 */}
          <PolicySection 
            id="cookies"
            title="7. Cookies & Tracking"
            content={
              <>
                <p className="mb-4">
                  Winglet uses cookies for essential functionality only:
                </p>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">•</span>
                    <span><strong className="text-white">Authentication Cookies:</strong> Keep you logged in securely.</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">•</span>
                    <span><strong className="text-white">Preference Cookies:</strong> Remember your theme (light/dark), timezone display, language.</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">•</span>
                    <span><strong className="text-white">Analytics (Optional):</strong> Basic page view tracking to improve user experience. You can opt out in Settings.</span>
                  </li>
                </ul>
                <p className="mt-4 text-gray-300">
                  We do NOT use third-party advertising cookies or trackers. No Facebook Pixel, no Google Ads tracking, no data brokers.
                </p>
              </>
            }
          />

          {/* Section 8 */}
          <PolicySection 
            id="children"
            title="8. Children's Privacy"
            content={
              <p className="text-gray-300">
                Winglet is designed for commercial pilots and aviation professionals. We do not knowingly collect data from anyone under 18. If you believe a child has created an account, please contact us immediately at <a href="mailto:privacy@wingletapp.com" className="text-[#4A90E2] hover:text-white">privacy@wingletapp.com</a>.
              </p>
            }
          />

          {/* Section 9 */}
          <PolicySection 
            id="changes"
            title="9. Changes to This Policy"
            content={
              <div>
                <p className="text-gray-300 mb-4">
                  We may update this Privacy Policy occasionally to reflect new features or legal requirements. If we make significant changes, we'll:
                </p>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">•</span>
                    <span>Email you 30 days before changes take effect</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">•</span>
                    <span>Display a notification in the app</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#4A90E2] mt-1">•</span>
                    <span>Update the "Last Updated" date at the top of this page</span>
                  </li>
                </ul>
                <p className="text-gray-300 mt-4">
                  Continued use of Winglet after changes take effect means you accept the new policy.
                </p>
              </div>
            }
          />

          {/* Section 10 */}
          <PolicySection 
            id="contact"
            title="10. Contact Us"
            content={
              <div>
                <p className="text-gray-300 mb-4">
                  Questions about privacy? Want to exercise your rights? Reach out:
                </p>
                <div className="bg-[#1A2B4A] border border-[#4A90E2]/30 rounded-lg p-6">
                  <div className="flex items-start space-x-4">
                    <Mail className="h-6 w-6 text-[#4A90E2] flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold mb-1">Email:</p>
                      <a href="mailto:privacy@wingletapp.com" className="text-[#4A90E2] hover:text-white text-lg">
                        privacy@wingletapp.com
                      </a>
                      <p className="text-gray-400 text-sm mt-2">
                        We'll respond within 48 hours for privacy-related inquiries.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            }
          />
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0F1824]">
        <div className="max-w-4xl mx-auto text-center">
          <Shield className="h-16 w-16 text-[#4A90E2] mx-auto mb-6" />
          <h2 className="text-3xl font-heading font-bold text-white mb-4">
            Your Data is Safe with Winglet
          </h2>
          <p className="text-gray-300 mb-8">
            Bank-level encryption, SOC 2 compliance, and a commitment to never sell your information.
          </p>
          <Link 
            to="/signup" 
            className="inline-block bg-[#4A90E2] hover:bg-[#3A7BC8] text-white px-8 py-4 rounded-lg font-semibold transition-all transform hover:scale-105"
          >
            Start Free Trial
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F1824] border-t border-gray-800 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Branding */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Plane className="h-6 w-6 text-[#4A90E2]" />
                <span className="text-xl font-heading font-bold text-white">Winglet</span>
              </div>
              <p className="text-gray-400 text-sm">The AI-powered co-pilot for your career.</p>
            </div>
            
            {/* Product Section */}
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/how-it-works" className="text-gray-400 hover:text-[#4A90E2] transition-colors">How It Works</Link></li>
                <li><Link to="/pricing" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Pricing</Link></li>
                <li><Link to="/compare-tools" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Compare</Link></li>
              </ul>
            </div>
            
            {/* Company Section */}
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="text-gray-400 hover:text-[#4A90E2] transition-colors">About Us</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Contact</Link></li>
                <li><Link to="/data-privacy" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Data Privacy</Link></li>
              </ul>
            </div>
            
            {/* Legal Section */}
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          {/* Copyright */}
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>© 2026 Winglet. Built by pilots, for pilots. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

const SummaryItem = ({ icon, title, description }) => (
  <div className="flex items-start space-x-3">
    <div className="flex-shrink-0 w-10 h-10 bg-[#4A90E2]/20 rounded-lg flex items-center justify-center text-[#4A90E2]">
      {icon}
    </div>
    <div>
      <h3 className="text-white font-semibold mb-1">{title}</h3>
      <p className="text-gray-300 text-sm">{description}</p>
    </div>
  </div>
);

const TOCLink = ({ href, text }) => (
  <a 
    href={href} 
    className="text-gray-300 hover:text-[#4A90E2] transition-colors flex items-center space-x-2"
  >
    <span className="w-2 h-2 bg-[#4A90E2]/50 rounded-full"></span>
    <span>{text}</span>
  </a>
);

const PolicySection = ({ id, title, content }) => (
  <div id={id} className="scroll-mt-24">
    <h2 className="text-3xl font-heading font-bold text-white mb-6">{title}</h2>
    <div className="text-gray-300 space-y-4">
      {content}
    </div>
  </div>
);

const DataCategory = ({ title, items }) => (
  <div className="bg-[#0F1824] border border-[#4A90E2]/20 rounded-lg p-4">
    <h4 className="text-white font-semibold mb-3">{title}</h4>
    <ul className="space-y-2">
      {items.map((item, index) => (
        <li key={index} className="flex items-start space-x-2 text-gray-300 text-sm">
          <span className="text-[#4A90E2] mt-0.5">•</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

const SecurityMeasure = ({ icon, title, description }) => (
  <div className="bg-[#0F1824] border border-[#4A90E2]/20 rounded-lg p-6 flex items-start space-x-4">
    <div className="flex-shrink-0 w-12 h-12 bg-[#4A90E2]/20 rounded-lg flex items-center justify-center text-[#4A90E2]">
      {icon}
    </div>
    <div>
      <h4 className="text-white font-semibold mb-2">{title}</h4>
      <p className="text-gray-300 text-sm">{description}</p>
    </div>
  </div>
);

const RightItem = ({ title, description }) => (
  <div className="bg-[#0F1824] border border-[#4A90E2]/20 rounded-lg p-4">
    <h4 className="text-white font-semibold mb-2">{title}</h4>
    <p className="text-gray-300 text-sm">{description}</p>
  </div>
);

export default PrivacyPolicy;
